import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
const RequestForm = React.lazy(() =>
  import("./RequestForm").then((module) => ({ default: module.RequestForm }))
) as React.FC<{ isGeneratingTitle?: boolean; type: "discuss" | "research" }>;
import { Button } from "../ui/button";
import { useUpdateRequest } from "@/hooks/useUpdateRequest";
import { Request as IRequest } from "@/types/research.types";
import {
  createRequestSchema,
  type CreateRequestFormValues,
} from "@/schemas/request-gallery.schemas";

interface EditRequestFormProps {
  request: IRequest;
  onSuccess?: () => void;
  onCancel?: () => void;
  type: "discuss" | "research";
}

export const EditRequestForm: React.FC<EditRequestFormProps> = ({
  request,
  onSuccess,
  onCancel,
  type,
}) => {
  const methods = useForm({
    resolver: zodResolver(createRequestSchema),
    defaultValues: {
      name: request.name,
      content: request.content,
      category_id: request.category_id ?? "",
      groups: request.groups,
      chain_with: request.chain_with.map((item) => ({ requestId: item })),
    },
  });

  const updateRequestMutation = useUpdateRequest();

  const onSubmit = async (data: CreateRequestFormValues) => {
    try {
      await updateRequestMutation.mutateAsync({
        id: request.id,
        type: type,
        ...data,
        category_id: data.category_id ?? null,
        chain_with: data.chain_with.map((item) => item.requestId),
        groups: data.groups,
      });
      onSuccess?.();
    } catch (error) {
      console.error("Error updating request:", error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-3">
          <React.Suspense fallback={<Loader className="animate-spin" />}>
            <RequestForm type={type} />
          </React.Suspense>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={updateRequestMutation.isPending}>
            {updateRequestMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
